import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import { Feature, Module, Permission } from '@core/configs/role';
import * as R from '__route';
import { TranslateService } from '__services';
import { UserProfileService } from '__services/user-profile.service';
import { link } from 'fs';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  module: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userProfile: UserProfileService,
    private translate: TranslateService,
  ) {}

  accessModule(moduleId: number): boolean {
    return this.checkAccess(moduleId);
  }

  accessFeature(
    featureId: number | Array<number>,
    moduleId: number = null,
    permission: number = null,
  ): boolean {
    return this.checkAccess(moduleId, featureId, permission);
  }

  hasView(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return this.checkPermission(Permission.VIEW, routeSnapshot);
  }

  hasAdd(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return this.checkPermission(Permission.ADD, routeSnapshot);
  }

  hasEdit(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return this.checkPermission(Permission.EDIT, routeSnapshot);
  }

  hasDelete(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return this.checkPermission(Permission.DELETE, routeSnapshot);
  }

  hasValidate(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return this.checkPermission(Permission.VALIDATE, routeSnapshot);
  }

  hasOverride(routeSnapshot: ActivatedRouteSnapshot): boolean {
    return this.checkPermission(Permission.OVERRIDE, routeSnapshot);
  }

  getMenus(): void {
    this.module = MENU;
    this.module.forEach((module) => {
      if (module.id === 'alert-module') {
        return;
      }
      module['access'] = this.accessModule(module.module_id);
      if (module['access']) {
        module.children.forEach((feature) => {
          feature['access'] = this.accessFeature(feature.id);
        });
      }
    });

    // Alert is always acceptable
    const alert = {
      id: 'alert-module',
      title: 'Alerts',
      description: 'Records that actions are needed by logged user.',
      link: '/alerts',
      class: 'alerts',
      access: true,
      order: 5,
      children: [
        {
          title: 'Alert and Notification',
          link: '/alerts/alerts-notification',
          access: true,
        },
        {
          title: 'Validation',
          link: '/alerts/validation',
          access: false,
        },
      ],
    };

    // if(this.userProfile.hasTruckingPersona() || this.userProfile.hasCarrierPersona()) {
    if (
      this.checkAccess(Module.ALERT, Feature.ALERT_VALIDATION, Permission.VIEW)
    ) {
      alert.children[1].access = true;
    }
    // alert.children[1].access = true;
    // }

    if (this.module.some((item) => item.id === 'alert-module')) {
      this.module.shift();
    }
    this.module.unshift(alert);
  }

  onNavToModule(selectedModule, activatedRoute, index) {
    if (`/${activatedRoute}` === selectedModule?.link) {
      return;
    }

    this.setActive(selectedModule, index);

    const childNav = selectedModule?.children.find((item) => item?.access);
    this.router.navigate([childNav?.link || '/']);
  }

  setActive(module, index) {
    if (module?.isActive) {
      return;
    }

    const existed = this.module.find((item) => item.isActive);
    if (existed) {
      existed.isActive = false;
    }

    const selected = this.module[index];
    selected['isActive'] = true;
  }

  public checkPermission(
    permission: number,
    routeSnapshot: ActivatedRouteSnapshot,
  ): boolean {
    // Make sure that there are moduleId and featureId in the routing module
    let moduleId;
    let featureId;
    for (const route of routeSnapshot.pathFromRoot) {
      if (route.data.module_id) {
        moduleId = route.data.module_id;
      }
      if (route.data.feature_id) {
        featureId = route.data.feature_id;
      }
    }
    return this.checkAccess(moduleId, featureId, permission);
  }

  public checkAccess(moduleId?, featureId?, permission?): boolean {
    const role = this.takeUserRole();
    if (!role || role.length === 0) {
      return false;
    }
    for (const module of role) {
      // Only check module
      if (module.module_id === moduleId && module.features.length > 0) {
        if (!featureId) {
          return true;
        } else {
          return this.checkFeatureAndPermission(
            module.features,
            featureId,
            permission,
          )
            ? true
            : false;
        }
      }

      // Only check feature
      if (featureId && !moduleId) {
        if (
          this.checkFeatureAndPermission(module.features, featureId, permission)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  public takeUserRole(): any {
    const role = JSON.parse(localStorage.getItem('userFeatures')) as Array<any>;
    return role;
  }

  public checkFeatureAndPermission(
    features: Array<any>,
    featureId: number | Array<number>,
    permission: number,
  ): boolean {
    if (typeof featureId === 'number') {
      featureId = [featureId];
    }
    for (const feature of features) {
      for (const id of featureId) {
        if (feature.id === id) {
          // Only check feature
          if (!permission) {
            return true;
          }

          // check the permission
          else if (permission & feature.perms) {
            return true;
          }
        }
      }
    }
  }
}

export const MENU: any = [
  {
    id: 'request-module',
    title: 'Requests',
    link: '/requests',
    class: 'requests',
    description: 'Allows user to send request to other personas.',
    order: 6,
    module_id: Module.REQUEST,
    children: [
      {
        title: 'Shipment',
        link: '/requests/shipment',
        id: Feature.REQUEST_SHIPMENT,
      },
      {
        title: 'Logistics Service Provider',
        link: '/requests/iso-tank',
        id: Feature.REQUEST_ISO_TANK_PROVIDER,
      },
      {
        title: 'Depot',
        link: '/requests/depot',
        id: Feature.REQUEST_DEPOT,
      },
      {
        title: 'Transport',
        link: '/requests/transport',
        id: Feature.REQUEST_TRANSPORT,
      },
    ],
  },
  {
    id: 'physical_transport-module',
    title: 'Active Jobs',
    link: '/physical-transport',
    class: 'physical-transport',
    description: 'Transport records of a company.',
    module_id: Module.PHYSICAL_TRANSPORT,
    order: 1,
    children: [
      {
        title: 'Journey List',
        link: '/physical-transport/journeys',
        id: Feature.JOURNEY_MANAGEMENT,
      },
      {
        title: 'Search',
        link: '/physical-transport/search',
        id: Feature.SEARCH,
      },
      {
        title: 'Report',
        link: '/physical-transport/report',
        id: Feature.REPORT,
      },
      {
        title: 'Report Master',
        link: '/physical-transport/report-master',
        id: Feature.REPORT_MASTER,
      },
    ],
  },
  {
    id: 'intermodal-module',
    title: 'Site Operations',
    link: '/intermodal',
    class: 'intermodal',
    description: 'Action tasks on company production plants.',
    module_id: Module.INTERMODAL,
    order: 3,
    children: [
      {
        title: 'Entry List',
        link: '/intermodal/entries',
        id: Feature.ENTRY_LIST,
      },
      {
        title: 'Weighbridge',
        link: '/intermodal/weighbridges',
        id: Feature.WEIGHBRIDGE_MANAGEMENT,
      },
      {
        title: 'Transfer',
        link: '/intermodal/eirs',
        id: [Feature.TRANSFER_EIR_MANAGEMENT, Feature.TRANSFER_BOL_MANAGEMENT],
      },
      {
        title: 'Container',
        link: '/intermodal/reheatings',
        id: Feature.CONTAINER_REHEATING_MANAGEMENT,
      },
      {
        title: 'Loading',
        link: '/intermodal/loadings',
        id: Feature.LOADING_MANAGEMENT,
      },
      {
        title: 'Load/Unload',
        link: '/intermodal/load-unload',
        id: Feature.LOAD_UNLOAD_MANAGEMENT,
      },
      {
        title: 'On/Off Loading',
        link: '/intermodal/on-loadings',
        id: Feature.ONLOADING_MANAGEMENT,
      },
      {
        title: 'Vessel',
        link: '/intermodal/vessel',
        id: Feature.VESSEL_MANAGEMENT,
      },
      {
        title: 'Cargo',
        link: R.CARGO_PF,
        id: Feature.CARGO_MANAGEMENT,
      },
      {
        title: 'DGD',
        link: R.DGD_PF,
        id: Feature.DGD_MANAGEMENT,
      },
    ],
  },
  {
    id: 'compliance-module',
    title: 'Job Management',
    link: '/compliance',
    class: 'documents',
    description: 'Management of flow of a journey.',
    module_id: Module.COMPLIANCE,
    order: 2,
    children: [
      {
        title: 'Flow Management',
        link: '/compliance/flows',
        id: Feature.FLOW_MANAGEMENT,
      },
      {
        title: 'Flow Validation',
        link: '/compliance/flows-validate',
        id: Feature.FLOW_VALIDATION,
      },
      {
        title: 'Document Management',
        link: '/compliance/documents',
        id: Feature.DOCUMENT_MANAGEMENT,
      },
      {
        title: 'Template Management',
        link: '/compliance/templates',
        id: Feature.TEMPLATE_MANAGEMENT,
      },
      {
        title: 'Verify Document',
        link: '/compliance/verify-document',
        id: Feature.VERIFY_DOCUMENT,
      },
      {
        title: 'Fleet',
        link: '/compliance/fleet',
        id: Feature.FLEET_MANAGEMENT,
      },
    ],
  },
  {
    id: 'regulatory-module',
    title: 'Import / Export Procedures',
    link: '/regulatory',
    class: 'documents',
    description: 'Management of regulatory transactions.',
    module_id: Module.REGULATORY,
    order: 4,
    children: [
      {
        title: 'Regulatory Dashboard',
        link: '/regulatory/dashboard',
        id: Feature.LOGCHAIN_REGULATORY_DASHBOARD,
      },
      {
        title: 'Regulatory Transaction',
        link: '/regulatory/list',
        id: Feature.LOGCHAIN_REGULATORY_FILING,
      },
    ],
  },
  {
    id: 'outbound-outhouse-shipment-module',
    title: 'Outbound Outhouse Shipment',
    link: '/outbound-outhouse-shipment',
    class: 'outbound-outhouse-shipment',
    description: 'Manage outbound outhouse shipment records.',
    module_id: Module.OUTBOUND_OUTHOUSE_SHIPMENT,
    order: 9,
    children: [
      {
        title: 'OOS Dashboard',
        link: '/outbound-outhouse-shipment/oos-dashboard',
        id: Feature.OOS_DASHBOARD,
      },
      {
        title: 'OOS Shipment Management',
        link: '/outbound-outhouse-shipment/oos-shipment',
        id: Feature.OOS_SHIPMENT,
      },
    ],
  },
  {
    id: 'admin-module',
    title: 'Admin',
    link: '/admin',
    class: 'user-gear',
    description: 'Management of Company info and User records.',
    module_id: Module.ADMIN,
    order: 7,
    children: [
      {
        title: 'Company',
        link: '/admin/company',
        id: Feature.COMPANY_MANAGEMENT,
      },
      {
        title: 'User',
        link: '/admin/users',
        id: Feature.USER_MANAGEMENT,
      },
      {
        title: 'Trade Lane',
        link: '/admin/trade-lanes',
        id: Feature.ADMIN_TRADE_LANE_MANAGEMENT,
      },
      {
        title: 'Party',
        link: '/admin/parties',
        id: Feature.PARTY_MANAGEMENT,
      },
      {
        title: 'Category Management',
        link: '/admin/category',
        id: Feature.CATEGORY_MANAGEMENT,
      },
    ],
  },
  {
    id: 'logchain_admin-module',
    title: 'LogChain Admin',
    link: '/logchain-admin',
    class: 'user-gear',
    description: 'Management of platform records.',
    module_id: Module.LOGCHAIN,
    child_class: 'sub-menu-bottom',
    order: 8,
    children: [
      {
        title: 'Company',
        link: '/logchain-admin/companies',
        id: Feature.LOGCHAIN_COMPANY_MANAGEMENT,
      },
      {
        title: 'User',
        link: '/logchain-admin/users',
        id: Feature.LOGCHAIN_USER_MANAGEMENT,
      },
      {
        title: 'Role',
        link: '/logchain-admin/roles',
        id: Feature.ROLE_MANAGEMENT,
      },
      {
        title: 'Trade Lane',
        link: '/logchain-admin/trade-lanes',
        id: Feature.TRADE_LANE_MANAGEMENT,
      },
      {
        title: 'Flow',
        link: '/logchain-admin/default-flow',
        id: Feature.LOGCHAIN_FLOW_MANAGEMENT,
      },
      {
        title: 'Regulatory',
        link: '/logchain-admin/transactions',
        id: Feature.LOGCHAIN_REGULATORY_FILING,
      },
      {
        title: 'Licence Management',
        link: '/logchain-admin/licence',
        id: Feature.LICENCE_MANAGEMENT,
      },
    ],
  },
];
